import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendChat } from "../api/client";
import { ApiError, type ChatResponse } from "../api/types";
import BookPickerDialog from "../components/BookPickerDialog";
import { useBooks } from "../hooks/useBooks";
import { useCategories } from "../hooks/useCategories";
import {
  ALL_SCOPE,
  PICKED_SCOPE,
  clearChatMessages,
  getChatMessages,
  getChatScope,
  getPickedBooks,
  setChatMessages,
  setChatScope,
  setPickedBooks,
  type ChatEntry,
} from "../lib/chatStorage";

export default function Chat() {
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.categories ?? [];

  const [scope, setScope] = useState<string>(() => getChatScope());
  const [picked, setPicked] = useState<string[]>(() => getPickedBooks());
  const [pickerOpen, setPickerOpen] = useState(false);
  // Only finished papers can be read, so only those are worth offering.
  const { data: booksData } = useBooks({ status: "completed", limit: 200 });
  const completedBooks = booksData?.books ?? [];
  const [messages, setMessages] = useState<ChatEntry[]>(() => getChatMessages(getChatScope()));
  const [input, setInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // If the persisted scope pointed at a category that's since been deleted, fall back to All books.
  useEffect(() => {
    if (!categoriesData) return;
    if (
      scope !== ALL_SCOPE &&
      scope !== PICKED_SCOPE &&
      !categoriesData.categories.some((c) => c.id === scope)
    ) {
      setScope(ALL_SCOPE);
      setChatScope(ALL_SCOPE);
      setMessages(getChatMessages(ALL_SCOPE));
    }
  }, [categoriesData, scope]);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    bottomRef.current?.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "end" });
  }, [messages.length]);

  const chatMutation = useMutation<ChatResponse, ApiError, ChatEntry[]>({
    mutationFn: (nextMessages) =>
      sendChat({
        category_id: scope === ALL_SCOPE || scope === PICKED_SCOPE ? null : scope,
        book_ids: scope === PICKED_SCOPE ? picked : undefined,
        messages: nextMessages.map(({ role, content }) => ({ role, content })),
      }),
  });

  function handleScopeChange(next: string) {
    // Choosing the picked scope with nothing picked yet goes straight to the
    // dialog, rather than leaving a scope that cannot answer anything.
    if (next === PICKED_SCOPE && picked.length === 0) {
      setPickerOpen(true);
      return;
    }
    setScope(next);
    setChatScope(next);
    setMessages(getChatMessages(next));
    setInput("");
    setErrorMessage(null);
    setErrorCode(null);
  }

  function handleClear() {
    clearChatMessages(scope);
    setMessages([]);
    setErrorMessage(null);
    setErrorCode(null);
  }

  function handleSend() {
    const content = input.trim();
    if (!content || chatMutation.isPending) return;

    const previous = messages;
    const nextMessages: ChatEntry[] = [...messages, { role: "user", content }];
    const sentScope = scope;

    setMessages(nextMessages);
    setChatMessages(sentScope, nextMessages);
    setInput("");
    setErrorMessage(null);
    setErrorCode(null);

    chatMutation.mutate(nextMessages, {
      onSuccess: (res) => {
        const withReply: ChatEntry[] = [...nextMessages, { role: "assistant", content: res.reply, sources: res.sources }];
        setMessages(withReply);
        setChatMessages(sentScope, withReply);
      },
      onError: (err) => {
        // Roll back the optimistic user turn — the backend never saw it, and keeping it around
        // would mean the next send replays two consecutive "user" turns.
        setMessages(previous);
        setChatMessages(sentScope, previous);
        setInput(content);
        if (err instanceof ApiError) {
          setErrorMessage(err.message);
          setErrorCode(err.code);
        } else {
          setErrorMessage("Something went wrong. Please try again.");
          setErrorCode(null);
        }
      },
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-semibold text-text">Chat</h1>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={scope}
            onChange={(e) => handleScopeChange(e.target.value)}
            disabled={chatMutation.isPending}
            aria-label="Chat scope"
            className="rounded-md border border-border bg-surface pl-3 pr-9 py-2 text-sm text-text disabled:opacity-50"
          >
            <option value={ALL_SCOPE}>All books</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value={PICKED_SCOPE}>
              {picked.length > 0 ? `${picked.length} chosen books` : "Choose books…"}
            </option>
          </select>
          {scope === PICKED_SCOPE ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={chatMutation.isPending}
              className="text-sm font-medium text-accent hover:text-accent-strong disabled:opacity-40"
            >
              Change
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleClear}
            disabled={messages.length === 0 || chatMutation.isPending}
            className="text-sm font-medium text-muted hover:text-text disabled:opacity-40"
          >
            Clear conversation
          </button>
        </div>
      </header>

      <BookPickerDialog
        open={pickerOpen}
        books={completedBooks}
        selected={picked}
        onClose={() => setPickerOpen(false)}
        onConfirm={(ids) => {
          setPicked(ids);
          setPickedBooks(ids);
          setPickerOpen(false);
          handleScopeChange(PICKED_SCOPE);
        }}
      />

      <div className="space-y-4 pb-4">
        {messages.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted">
            Ask something about the books in this scope. Boort answers using only what's inside them.
          </p>
        ) : (
          messages.map((message, i) => <MessageBubble key={i} message={message} />)
        )}

        {chatMutation.isPending ? (
          <div className="mr-auto max-w-[80%] rounded-lg border border-border bg-surface p-4">
            <p className="animate-pulse-soft text-sm text-muted">Reading your books…</p>
          </div>
        ) : null}

        {errorMessage ? (
          errorCode === "EMPTY_SCOPE" ? (
            <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-muted">{errorMessage}</div>
          ) : (
            <p className="text-sm text-danger">{errorMessage}</p>
          )
        ) : null}

        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-0 border-t border-border bg-bg pb-2 pt-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={chatMutation.isPending}
            rows={2}
            placeholder="Ask about the books in this scope… (Enter to send, Shift+Enter for a new line)"
            aria-label="Chat message"
            className="flex-1 resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={chatMutation.isPending || !input.trim()}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {chatMutation.isPending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatEntry }) {
  if (message.role === "user") {
    return (
      <div className="ml-auto max-w-[80%] rounded-lg bg-accent-soft px-4 py-2.5 text-sm text-text">
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
    );
  }

  return (
    <div className="mr-auto max-w-[80%] rounded-lg border border-border bg-surface p-4">
      <div className="chat-content text-sm text-text">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
      </div>
      {message.sources && message.sources.length > 0 ? (
        <p className="mt-2 text-xs text-muted">From: {message.sources.map((s) => s.title).join(", ")}</p>
      ) : null}
    </div>
  );
}
